import { Controller } from "@hotwired/stimulus"

// Shows the fields the chosen transaction type actually has (C21). One form
// records all eleven kinds, and most of them share nothing: a 1-for-10
// consolidation has no price and no brokerage, a capital return has no share
// count, a reconstruction names a second security nobody else does.
//
// Progressive enhancement, deliberately: the server renders EVERY group, and
// this controller hides the ones the current kind does not use. With
// JavaScript off the member sees all of them and the model's own validations
// decide what was required — which is why nothing here is the rule for
// anything. `hidden`, not `style.display`, so a hidden group is hidden from
// assistive tech and from Capybara too (and so the app's own `[hidden]` rules
// hold).
//
// Requiredness is applied here rather than baked into the markup for the same
// reason: a static `required` on the price box would stop a JS-off member
// submitting a consolidation, which has no price at all. The browser hint
// follows the mode; the server validates regardless.
//
// The kind → mode map is supplied by the server (PortfoliosHelper#
// trade_kind_modes, built from PortfolioTrade's own constants and predicates).
// A copy of that list in JS is the one that goes stale the day a kind is added.
//
//   data-controller="trade-kind"
//   data-trade-kind-modes-value='{"buy":"acquiring","split":"restating",…}'
//   targets: kind, group, label
//   data-trade-kind-modes="acquiring disposing"  (on a group)
//   data-trade-kind-labels='{"cost_base":"Amount per share ($)"}'  (on a label)
//   data-trade-kind-required-modes="acquiring disposing"  (on an input)
export default class extends Controller {
  static targets = ["kind", "group", "label"]
  static values = { modes: Object }

  connect() {
    // Captured once, before anything is rewritten: a label's default text is
    // whatever the server rendered, so the map only has to carry the
    // EXCEPTIONS and a mode with no entry falls back to the original wording.
    this.defaultLabels = new WeakMap()
    this.labelOverrides = new WeakMap()
    this.labelTargets.forEach((label) => {
      this.defaultLabels.set(label, label.textContent)
      this.labelOverrides.set(label, this.parseOverrides(label))
    })

    this.apply()
  }

  apply() {
    const mode = this.currentMode()

    this.groupTargets.forEach((group) => {
      group.hidden = !this.modesOf(group.dataset.tradeKindModes).includes(mode)
      this.applyRequired(group, mode)
    })

    this.labelTargets.forEach((label) => this.relabel(label, mode))
  }

  // The mode of the selected kind, or null when the select is missing or the
  // server did not list the kind (a hand-rolled value, or a stale bundle
  // against a newer server). Null shows nothing conditional rather than
  // everything: a form showing a price box AND a ratio box says the kind means
  // both, and one of the two would be silently discarded on save.
  currentMode() {
    if (!this.hasKindTarget) return null

    const modes = this.modesValue
    return (modes && modes[this.kindTarget.value]) || null
  }

  modesOf(attribute) {
    return (attribute || "").split(/\s+/).filter(Boolean)
  }

  // A hidden required field blocks submit with a validation bubble pointing at
  // a box the member cannot see — the browser's own "not focusable" dead end.
  // So requiredness is only ever asserted on a VISIBLE group, and within one,
  // only for the modes the field is genuinely required in (a split's share
  // count is optional; a reconstruction's is not, and they share a box).
  applyRequired(group, mode) {
    group.querySelectorAll("[data-trade-kind-required-modes]").forEach((field) => {
      field.required = !group.hidden && this.modesOf(field.dataset.tradeKindRequiredModes).includes(mode)
    })
  }

  relabel(label, mode) {
    label.textContent = this.labelOverrides.get(label)[mode] || this.defaultLabels.get(label)
  }

  // Parsed once, at connect: the markup does not change under a kind change.
  parseOverrides(label) {
    try {
      return JSON.parse(label.dataset.tradeKindLabels || "{}")
    } catch {
      // Malformed markup must not take the form down: the label keeps the
      // wording the server rendered, which is always a true one.
      return {}
    }
  }
}
